import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Truck, CheckCircle, Wallet } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';
import { isAuthenticated, getCurrentUser } from '../lib/authUtils';
import { createOrder } from '../services/orderService';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Shipping Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    // Payment Info
    paymentMethod: 'card',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    // Delivery
    shippingMethod: 'standard',
    notes: ''
  });

  const shippingCost = formData.shippingMethod === 'express' ? 25 : getCartTotal() >= 50 ? 0 : 10;
  const tax = getCartTotal() * 0.1;
  const total = getCartTotal() + shippingCost + tax;

  // Get trade credit info from logged in user
  const tradeCredit = useMemo(() => {
    const user = getCurrentUser();
    const tc = user?.customerAccount?.tradeCredit;
    if (!tc) return null;
    return {
      tradeCreditId: tc.tradeCreditId,
      allocated: parseFloat(tc.tradeCreditAllocated || 0),
      used: parseFloat(tc.tradeCreditUsed || 0),
      balance: parseFloat(tc.balanceTradeCredit || 0),
      status: tc.status,
    };
  }, []);

  const hasSufficientCredit = tradeCredit && tradeCredit.balance >= total;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Validate shipping form before proceeding to payment
  const validateShippingForm = () => {
    const required = ['firstName', 'lastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode', 'country'];
    for (const field of required) {
      if (!formData[field] || formData[field].trim() === '') {
        toast({
          title: 'Missing Information',
          description: `Please fill in all required shipping fields.`,
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const handleContinueToPayment = () => {
    if (validateShippingForm()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate on account payment
    if (formData.paymentMethod === 'onAccount') {
      if (!tradeCredit || !hasSufficientCredit) {
        toast({
          title: 'Insufficient Trade Credit',
          description: 'You do not have enough trade credit balance for this order.',
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      const user = getCurrentUser();
      
      // Build order payload matching the API specification
      const orderPayload = {
        tradeCreditId: formData.paymentMethod === 'onAccount' && tradeCredit ? tradeCredit.tradeCreditId : 0,
        salesOrderNumber: '',
        purchaseOrderNumber: '',
        Channel: 'web',
        packingSlipNumber: '',
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        email: formData.email,
        customerCountry: formData.country,
        customerState: formData.state,
        customerCity: formData.city,
        customerStreet: formData.address,
        customerZipcode: formData.zipCode,
        customerAccountId: user?.customerAccountId || 0,
        orderStatus: 'pending',
        shippingMethod: formData.shippingMethod,
        shippingCharges: shippingCost.toFixed(2),
        billingCountry: formData.country,
        billingState: formData.state,
        billingCity: formData.city,
        billingStreet: formData.address,
        billingZipcode: formData.zipCode,
        shippingCountry: formData.country,
        paymentType: formData.paymentMethod,
        onAccountTransactionNumber: 0,
        maskedCardNumber: formData.paymentMethod === 'card' ? parseInt(formData.cardNumber.slice(-4)) || 0 : 0,
        shippingState: formData.state,
        shippingCity: formData.city,
        shippingStreet: formData.address,
        shippingZipcode: formData.zipCode,
        subTotal: getCartTotal().toFixed(2),
        shippingCost: shippingCost.toFixed(2),
        discount: '0',
        GST: tax.toFixed(2),
        totalPrice: total.toFixed(2),
        notes: formData.notes,
        addProducts: cart.map(item => item.id?.toString() || item.sku),
      };

      // Call the createOrder API
      const response = await createOrder(orderPayload);
      console.log('Order created:', response);

      toast({
        title: 'Order Placed Successfully!',
        description: formData.paymentMethod === 'onAccount' 
          ? `Your order has been confirmed. Trade credit deducted: $${total.toFixed(2)}`
          : 'Your order has been confirmed.'
      });
      
      clearCart();
      setTimeout(() => {
        navigate('/orders');
      }, 1500);
    } catch (error) {
      console.error('Order error:', error);
      toast({
        title: 'Order Failed',
        description: error.message || 'Failed to place order. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Check authentication on mount
  useEffect(() => {
    if (!isAuthenticated()) {
      toast({
        title: 'Login Required',
        description: 'Please login to proceed to checkout.',
        variant: 'destructive',
      });
      navigate('/customer-login', { state: { from: { pathname: '/checkout' } } });
    }
  }, [navigate]);

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 1 ? 'bg-red-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                1
              </div>
              <span className="ml-2 font-semibold">Shipping</span>
            </div>
            <div className={`w-24 h-1 mx-4 ${
              step >= 2 ? 'bg-red-600' : 'bg-gray-300'
            }`}></div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 2 ? 'bg-red-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                2
              </div>
              <span className="ml-2 font-semibold">Payment</span>
            </div>
            <div className={`w-24 h-1 mx-4 ${
              step >= 3 ? 'bg-red-600' : 'bg-gray-300'
            }`}></div>
            <div className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                step >= 3 ? 'bg-red-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                3
              </div>
              <span className="ml-2 font-semibold">Review</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Step 1: Shipping Information */}
              {step === 1 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <Truck className="mr-2 text-red-600" size={24} />
                    Shipping Information
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        ZIP Code *
                      </label>
                      <input
                        type="text"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country *
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                      >
                        <option>United States</option>
                        <option>Canada</option>
                        <option>Mexico</option>
                      </select>
                    </div>
                  </div>

                  {/* Shipping Method */}
                  <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Shipping Method</h3>
                    <div className="space-y-2">
                      <label className="flex items-center p-4 border rounded cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="standard"
                          checked={formData.shippingMethod === 'standard'}
                          onChange={handleInputChange}
                          className="mr-3 accent-red-600"
                        />
                        <div className="flex-1">
                          <div className="font-semibold">Standard Shipping (5-7 days)</div>
                          <div className="text-sm text-gray-600">Free for orders over $50</div>
                        </div>
                        <div className="font-bold">
                          {getCartTotal() >= 50 ? 'FREE' : '$10.00'}
                        </div>
                      </label>
                      <label className="flex items-center p-4 border rounded cursor-pointer hover:bg-gray-50">
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="express"
                          checked={formData.shippingMethod === 'express'}
                          onChange={handleInputChange}
                          className="mr-3 accent-red-600"
                        />
                        <div className="flex-1">
                          <div className="font-semibold">Express Shipping (2-3 days)</div>
                          <div className="text-sm text-gray-600">Fast delivery</div>
                        </div>
                        <div className="font-bold">$25.00</div>
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleContinueToPayment}
                    className="mt-6 w-full bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold"
                  >
                    Continue to Payment
                  </button>
                </div>
              )}

              {/* Step 2: Payment Information */}
              {step === 2 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <CreditCard className="mr-2 text-red-600" size={24} />
                    Payment Information
                  </h2>

                  <div className="mb-6">
                    <label className="flex items-center p-4 border rounded cursor-pointer hover:bg-gray-50 mb-2">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === 'card'}
                        onChange={handleInputChange}
                        className="mr-3 accent-red-600"
                      />
                      <span className="font-semibold">Credit / Debit Card</span>
                    </label>
                    <label className="flex items-center p-4 border rounded cursor-pointer hover:bg-gray-50 mb-2">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="po"
                        checked={formData.paymentMethod === 'po'}
                        onChange={handleInputChange}
                        className="mr-3 accent-red-600"
                      />
                      <span className="font-semibold">Purchase Order</span>
                    </label>
                    {tradeCredit && (
                      <label className={`flex items-center p-4 border rounded cursor-pointer hover:bg-gray-50 ${!hasSufficientCredit ? 'opacity-60' : ''}`}>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="onAccount"
                          checked={formData.paymentMethod === 'onAccount'}
                          onChange={handleInputChange}
                          disabled={!hasSufficientCredit}
                          className="mr-3 accent-red-600"
                        />
                        <div className="flex-1">
                          <span className="font-semibold">On Account (Trade Credit)</span>
                          <div className="text-sm text-gray-600 mt-1">
                            <span className="flex items-center">
                              <Wallet size={14} className="mr-1" />
                              Balance: <span className={`font-semibold ml-1 ${hasSufficientCredit ? 'text-green-600' : 'text-red-600'}`}>
                                ${tradeCredit.balance.toFixed(2)}
                              </span>
                            </span>
                            {!hasSufficientCredit && (
                              <span className="text-red-600 text-xs">Insufficient balance for this order</span>
                            )}
                          </div>
                        </div>
                      </label>
                    )}
                  </div>

                  {formData.paymentMethod === 'onAccount' && tradeCredit && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
                      <h4 className="font-semibold text-blue-800 mb-2">Trade Credit Summary</h4>
                      <div className="text-sm text-blue-700 space-y-1">
                        <div className="flex justify-between">
                          <span>Current Balance:</span>
                          <span className="font-semibold">${tradeCredit.balance.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Order Total:</span>
                          <span className="font-semibold">-${total.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                          <span>Remaining Balance:</span>
                          <span className="font-semibold">${(tradeCredit.balance - total).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Card Number *
                        </label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          placeholder="1234 5678 9012 3456"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Cardholder Name *
                        </label>
                        <input
                          type="text"
                          name="cardName"
                          value={formData.cardName}
                          onChange={handleInputChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Expiry Date *
                          </label>
                          <input
                            type="text"
                            name="expiryDate"
                            value={formData.expiryDate}
                            onChange={handleInputChange}
                            placeholder="MM/YY"
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            CVV *
                          </label>
                          <input
                            type="text"
                            name="cvv"
                            value={formData.cvv}
                            onChange={handleInputChange}
                            placeholder="123"
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-red-500"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="flex-1 border border-gray-300 py-3 rounded hover:bg-gray-50 transition font-semibold"
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="flex-1 bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold"
                    >
                      Review Order
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Review Order */}
              {step === 3 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                    <CheckCircle className="mr-2 text-red-600" size={24} />
                    Review Your Order
                  </h2>

                  <div className="space-y-6">
                    {/* Shipping Details */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Shipping Address</h3>
                      <p className="text-gray-600">
                        {formData.firstName} {formData.lastName}<br />
                        {formData.address}<br />
                        {formData.city}, {formData.state} {formData.zipCode}<br />
                        {formData.country}<br />
                        Phone: {formData.phone}
                      </p>
                    </div>

                    {/* Order Items */}
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Order Items</h3>
                      <div className="space-y-2">
                        {cart.map((item) => (
                          <div key={item.id} className="flex justify-between text-sm">
                            <span>{item.name} x {item.quantity}</span>
                            <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-4 mt-6">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 border border-gray-300 py-3 rounded hover:bg-gray-50 transition font-semibold"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 bg-red-600 text-white py-3 rounded hover:bg-red-700 transition font-semibold"
                    >
                      Place Order
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div>
              <div className="bg-white rounded-lg shadow p-6 sticky top-24">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6">
                  {cart.map((item) => (
                    <div key={item.id} className="flex items-center space-x-3">
                      <img 
                        src={item.image || '/placeholder.jpg'} 
                        alt={item.name} 
                        onError={(e) => {
                          e.target.src = '/placeholder.jpg';
                        }}
                        className="w-16 h-16 object-cover rounded" 
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 border-t pt-4">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Shipping</span>
                    <span className="font-semibold">
                      {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                    </span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span className="font-semibold">${tax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-gray-900 border-t pt-2">
                    <span>Total</span>
                    <span>${total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
